import React, { useMemo } from 'react'
import styles from './AllProjectsItemCard.module.scss'
import { CalendarDays, User, MapPin, HardHat, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import defaultImage from '../../../assets/images/foto-bienvenida.jpg'
import usePremiumCheck from '../../../hooks/usePremiumCheck'
import { clearSubProjectBudgets } from '../../../store/actions/profileActions'

type ProjectImage = {
  image?: string | null
} | null

type SubProject = {
  name?: string | null
} | null

type ProjectUser = {
  type?: string | null
  razon_social?: string | null
  name?: string | null
  last_name?: string | null
  username?: string | null
} | null

type ProjectItem = {
  id: number | string
  name?: string | null
  startDate?: string | null
  endDate?: string | null
  zone?: string | null
  user?: ProjectUser
  description?: string | null
  image?: string | null
  proyecto_images?: ProjectImage[] | null
  subproyecto?: SubProject[] | null
}

interface AllProjectsItemCardProps {
  item: ProjectItem
  clearSubProjectBudgets: () => void
}

interface RootState {
  general?: {
    userData?: unknown
  }
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const day = date.getUTCDate().toString().padStart(2, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

const AllProjectsItemCard: React.FC<AllProjectsItemCardProps> = ({ item, clearSubProjectBudgets }) => {
  const navigate = useNavigate()
  const premiumCheck = usePremiumCheck()

  const coverImage = useMemo(() => {
    if (Array.isArray(item?.proyecto_images) && item.proyecto_images.length > 0) {
      const imageSource = item.proyecto_images[0]?.image
      return imageSource || defaultImage
    }
    if (item?.image) {
      return item.image
    }
    return defaultImage
  }, [item])

  const ownerName = useMemo(() => {
    const projectUser = item?.user
    if (!projectUser) {
      return ''
    }
    if (projectUser.type === 'COMPANY') {
      return projectUser.razon_social || ''
    }
    const firstName = projectUser.name || ''
    const lastName = projectUser.last_name || ''
    return `${firstName} ${lastName}`.trim()
  }, [item])

  const subprojects = useMemo(() => {
    if (!Array.isArray(item?.subproyecto)) {
      return [] as string[]
    }
    return item.subproyecto
      .map(subproject => (subproject?.name ? String(subproject.name) : null))
      .filter((name): name is string => Boolean(name))
  }, [item])

  const startDate = formatDate(item?.startDate)
  const endDate = formatDate(item?.endDate)

  const handleOpen = () => {
    clearSubProjectBudgets()
    navigate(`/obrasprivadas/${item.id}`)
  }

  return (
    <article className={styles.card} style={{ '--card-cover': `url("${coverImage || '/placeholder.svg'}")` } as React.CSSProperties}>
      <div className={styles.media}>
        <img className={styles.mediaImage} src={coverImage || '/placeholder.svg'} alt={`Imagen de ${item?.name || 'proyecto'}`} />
      </div>
      <div className={styles.body}>
        <div className={styles.header}>
          <h3 className={styles.title}>{item?.name}</h3>
          {item?.description && <p className={styles.description}>{item.description}</p>}
          <div className={styles.owner}>
            <div className={styles.ownerIcon}>
              <User size={18} />
            </div>
            <div className={styles.ownerInfo}>
              <span className={styles.ownerLabel}>Propietario</span>
              <span className={styles.ownerName}>{ownerName}</span>
            </div>
          </div>
        </div>
        <div className={styles.infoSection}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <MapPin size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Ubicación</span>
                <span className={styles.infoValue}>{item?.zone || 'Sin especificar'}</span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <User size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Usuario</span>
                <span className={styles.infoValue}>{item?.user?.username || 'Sin especificar'}</span>
              </div>
            </div>
          </div>
          {subprojects.length > 0 && (
            <div className={styles.tags}>
              {subprojects.map(subproject => (
                <span key={subproject} className={styles.tag}>
                  {subproject}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className={styles.highlight}>
          <div className={styles.highlightRow}>
            <div className={styles.highlightIcon}>
              <HardHat size={18} />
            </div>
            <div className={styles.highlightContent}>
              <span className={styles.highlightLabel}>Rubros del proyecto</span>
              <span className={styles.highlightValue}>{subprojects.length > 0 ? `${subprojects.length} rubros` : 'Sin rubros asociados'}</span>
            </div>
          </div>
          <div className={styles.highlightRow}>
            <div className={styles.highlightIcon}>
              <CalendarDays size={18} />
            </div>
            <div className={styles.highlightContent}>
              <span className={styles.highlightLabel}>Período del proyecto</span>
              <span className={styles.highlightValue}>
                {startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || 'Sin fecha definida'}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <button className={styles.cta} onClick={premiumCheck(handleOpen)} aria-label={`Ver más detalles de ${item?.name || 'proyecto'}`}>
            <span>Ver más</span>
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    </article>
  )
}

const mapStateToProps = (state: RootState) => ({
  userData: state?.general?.userData,
})

export default connect(mapStateToProps, { clearSubProjectBudgets })(AllProjectsItemCard)

